import { Dialect } from '../dialect.js';

/**
 * Δεσμευμένες λέξεις — υπερσύνολο ΓΛΩΣΣΑΣ και Ψευδογλώσσας.
 *
 * Και οι δύο γλώσσες μοιράζονται σχεδόν όλο το λεξιλόγιο (ΑΝ/ΤΟΤΕ/ΑΛΛΙΩΣ,
 * ΓΙΑ/ΑΠΟ/ΜΕΧΡΙ, ΟΣΟ/ΕΠΑΝΑΛΑΒΕ, ΔΙΑΒΑΣΕ/ΓΡΑΨΕ, ← κ.λπ.). Οι διαφορές τους
 * είναι κυρίως δομικές/συντακτικές (π.χ. αν απαιτείται ενότητα ΜΕΤΑΒΛΗΤΕΣ ή
 * όχι) και επιλύονται στον Parser — ΕΚΤΟΣ από μια χούφτα λέξεων που
 * υπάρχουν αποκλειστικά στη μία διάλεκτο (βλ. `GLOSSA_ONLY_KEYWORDS`/
 * `PSEUDOGLOSSA_ONLY_KEYWORDS`) — αυτές δεν πρέπει να δεσμεύονται καθόλου
 * στην ΑΛΛΗ διάλεκτο, αλλιώς ένα πρόγραμμα ΓΛΩΣΣΑΣ δεν θα μπορούσε να έχει
 * π.χ. μεταβλητή με όνομα "Δεδομένα" (άσχετη λέξη εκεί).
 *
 * Η αντιστοίχιση λέξεων γίνεται χωρίς διάκριση πεζών/κεφαλαίων και χωρίς
 * διάκριση τόνων, ώστε ο διερμηνευτής να είναι ανεκτικός σε μαθητικό
 * πληκτρολόγιο (π.χ. "πρόγραμμα", "ΠΡΟΓΡΑΜΜΑ" και "Πρόγραμμα" είναι ισοδύναμα).
 */

export const KEYWORD_LIST = [
  // Δομή προγράμματος / αλγορίθμου
  'ΠΡΟΓΡΑΜΜΑ',
  'ΑΛΓΟΡΙΘΜΟΣ',
  'ΔΕΔΟΜΕΝΑ',
  'ΑΠΟΤΕΛΕΣΜΑΤΑ',
  'ΣΤΑΘΕΡΕΣ',
  'ΜΕΤΑΒΛΗΤΕΣ',
  'ΑΚΕΡΑΙΕΣ',
  'ΠΡΑΓΜΑΤΙΚΕΣ',
  'ΧΑΡΑΚΤΗΡΕΣ',
  'ΛΟΓΙΚΕΣ',
  // Ενικές μορφές — χρησιμοποιούνται στον τύπο επιστροφής συνάρτησης,
  // π.χ. "ΣΥΝΑΡΤΗΣΗ Χ(...): ΑΚΕΡΑΙΑ"
  'ΑΚΕΡΑΙΑ',
  'ΠΡΑΓΜΑΤΙΚΗ',
  'ΧΑΡΑΚΤΗΡΑΣ',
  'ΛΟΓΙΚΗ',
  'ΑΡΧΗ',
  'ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ',
  // Στην Ψευδογλώσσα το τέλος του αλγορίθμου γράφεται "Τέλος <Όνομα>" — απλό
  // ΤΕΛΟΣ ακολουθούμενο από identifier, όχι ξεχωριστή λέξη ΤΕΛΟΣ_ΑΛΓΟΡΙΘΜΟΥ.
  'ΤΕΛΟΣ',

  // Υποπρογράμματα
  'ΔΙΑΔΙΚΑΣΙΑ',
  'ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ',
  'ΣΥΝΑΡΤΗΣΗ',
  'ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ',
  'ΚΑΛΕΣΕ',

  // Είσοδος / έξοδος
  'ΔΙΑΒΑΣΕ',
  'ΓΡΑΨΕ',
  'ΕΚΤΥΠΩΣΕ',
  'ΕΜΦΑΝΙΣΕ',
  'ΑΝΤΙΜΕΤΑΘΕΣΕ',

  // Επιλογή
  'ΑΝ',
  'ΤΟΤΕ',
  'ΑΛΛΙΩΣ_ΑΝ',
  'ΑΛΛΙΩΣ',
  'ΤΕΛΟΣ_ΑΝ',

  // Πολλαπλή επιλογή — "ΠΕΡΙΠΤΩΣΗ ΑΛΛΙΩΣ" δεν χρειάζεται δική του λέξη·
  // είναι απλώς ΠΕΡΙΠΤΩΣΗ ακολουθούμενο από το ήδη υπάρχον ΑΛΛΙΩΣ.
  'ΕΠΙΛΕΞΕ',
  'ΠΕΡΙΠΤΩΣΗ',
  'ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ',

  // Επανάληψη
  'ΓΙΑ',
  'ΑΠΟ',
  'ΜΕΧΡΙ',
  'ΜΕ_ΒΗΜΑ',
  'ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ',
  'ΟΣΟ',
  'ΕΠΑΝΑΛΑΒΕ',
  'ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ',
  'ΜΕΧΡΙΣ_ΟΤΟΥ',

  // Τελεστές-λέξεις
  'ΚΑΙ',
  'Η', // "Ή" (λογικό OR) — αποθηκεύεται χωρίς τόνο, βλ. σχόλιο κανονικοποίησης
  'ΟΧΙ',
  'DIV',
  'MOD',

  // Λογικές τιμές
  'ΑΛΗΘΗΣ',
  'ΨΕΥΔΗΣ',
] as const;

export type Keyword = (typeof KEYWORD_LIST)[number];

/** Λέξεις που υπάρχουν ΜΟΝΟ στη δομή ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ της ΓΛΩΣΣΑΣ — άσχετες/ελεύθερες ως αναγνωριστικά στην Ψευδογλώσσα. */
const GLOSSA_ONLY_KEYWORDS: ReadonlySet<Keyword> = new Set([
  'ΠΡΟΓΡΑΜΜΑ',
  'ΣΤΑΘΕΡΕΣ',
  'ΜΕΤΑΒΛΗΤΕΣ',
  'ΑΚΕΡΑΙΕΣ',
  'ΠΡΑΓΜΑΤΙΚΕΣ',
  'ΧΑΡΑΚΤΗΡΕΣ',
  'ΛΟΓΙΚΕΣ',
  'ΑΚΕΡΑΙΑ',
  'ΠΡΑΓΜΑΤΙΚΗ',
  'ΧΑΡΑΚΤΗΡΑΣ',
  'ΛΟΓΙΚΗ',
  'ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ',
  // Υποπρογράμματα — μόνο ΓΛΩΣΣΑ (βλ. interpreter.ts/parser.ts).
  'ΔΙΑΔΙΚΑΣΙΑ',
  'ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ',
  'ΣΥΝΑΡΤΗΣΗ',
  'ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ',
  'ΚΑΛΕΣΕ',
]);

/** Λέξεις που υπάρχουν ΜΟΝΟ στη δομή Αλγόριθμος...Τέλος της Ψευδογλώσσας — άσχετες/ελεύθερες ως αναγνωριστικά στη ΓΛΩΣΣΑ. */
const PSEUDOGLOSSA_ONLY_KEYWORDS: ReadonlySet<Keyword> = new Set([
  'ΑΛΓΟΡΙΘΜΟΣ',
  'ΔΕΔΟΜΕΝΑ',
  'ΑΠΟΤΕΛΕΣΜΑΤΑ',
  'ΤΕΛΟΣ',
  'ΕΚΤΥΠΩΣΕ',
  'ΕΜΦΑΝΙΣΕ',
  'ΑΝΤΙΜΕΤΑΘΕΣΕ',
]);

/**
 * Αφαιρεί τόνους/διαλυτικά (τονικά σημάδια) από ελληνικό (ή λατινικό) κείμενο
 * και το μετατρέπει σε κεφαλαία, ώστε να συγκρίνεται με το KEYWORD_LIST.
 * Το τελικό σίγμα (ς) μετατρέπεται ρητά σε σίγμα (σ) πριν τα κεφαλαία, για
 * συνέπεια ανεξαρτήτως runtime.
 */
export function normalizeKeywordText(text: string): string {
  const upper = text.replace(/ς/g, 'σ').toUpperCase();
  const decomposed = upper.normalize('NFD');

  // Αφαιρούμε τους χαρακτήρες συνδυασμού (combining diacritical marks,
  // code points 0x0300-0x036F) που προκύπτουν από την NFD αποσύνθεση —
  // π.χ. ο τόνος στο "Ή" γίνεται ξεχωριστός χαρακτήρας U+0301.
  let result = '';
  for (const ch of decomposed) {
    const codePoint = ch.codePointAt(0) ?? 0;
    const isCombiningMark = codePoint >= 0x0300 && codePoint <= 0x036f;
    if (!isCombiningMark) {
      result += ch;
    }
  }

  return result.normalize('NFC');
}

const KEYWORD_SET: ReadonlySet<string> = new Set(KEYWORD_LIST);

/**
 * Επιστρέφει την κανονική μορφή της λέξης αν είναι δεσμευμένη, αλλιώς
 * undefined. Αν δοθεί `dialect`, μια λέξη αποκλειστική της ΑΛΛΗΣ διαλέκτου
 * (π.χ. "Δεδομένα" ενώ `dialect === Dialect.GLOSSA`) αγνοείται — επιστρέφει
 * undefined, ώστε να «πέσει» σε απλό IDENTIFIER αντί να δεσμευτεί άδικα.
 * Χωρίς `dialect` (προεπιλογή) αναγνωρίζει το πλήρες υπερσύνολο, όπως πριν.
 */
export function lookupKeyword(text: string, dialect?: Dialect): Keyword | undefined {
  const normalized = normalizeKeywordText(text);
  if (!KEYWORD_SET.has(normalized)) return undefined;
  const keyword = normalized as Keyword;
  if (dialect === Dialect.GLOSSA && PSEUDOGLOSSA_ONLY_KEYWORDS.has(keyword)) return undefined;
  if (dialect === Dialect.PSEUDOGLOSSA && GLOSSA_ONLY_KEYWORDS.has(keyword)) return undefined;
  return keyword;
}

/**
 * Οι λέξεις-κλειδιά που είναι πράγματι έγκυρες στη δοσμένη διάλεκτο (το
 * υπερσύνολο μείον τις αποκλειστικές της άλλης) — χρήσιμο για οτιδήποτε
 * χρειάζεται να απαριθμήσει "όλες τις λέξεις-κλειδιά αυτής της διαλέκτου"
 * αντί να ελέγξει μία συγκεκριμένη (π.χ. keyword palette, syntax
 * highlighting, αυτόματη συμπλήρωση στο Web UI).
 */
export function keywordsForDialect(dialect: Dialect): readonly Keyword[] {
  const exclude = dialect === Dialect.GLOSSA ? PSEUDOGLOSSA_ONLY_KEYWORDS : GLOSSA_ONLY_KEYWORDS;
  return KEYWORD_LIST.filter((kw) => !exclude.has(kw));
}
